package gr.tech.site.pages.landings

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.components.sections.RelatedSection
import gr.tech.site.pages.SitePage
import gr.tech.site.styles.Theme
import gr.tech.site.styles.landings.*
import gr.tech.site.util.ImageRepo
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initCannabisRebatesPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "Cannabis & Marijuana Rebates - Up to 100% on LEDs, HVAC & More",
        description = "Cannabis cultivators can recover up to 100% on LED grow lights, HVAC, and dehumidification through utility rebate programs. Green Rebates specializes in cannabis grows nationwide.",
        canonicalUrl = "https://greenrebates.com/cannabis-rebates",
        ogImage = "/images/cannabiscloseup1.webp"
    ))
}

@Page(SitePage.Landing.CANNABIS_REBATES)
@Layout(".components.layouts.PageLayout")
@Composable
fun CannabisRebatesPage() {
    Main(
        attrs = Modifier
            .classNames("d-flex", "flex-column")
            .fillMaxSize()
            .role("main")
            .toAttrs()
    ) {

        // HERO
        Section(
            attrs = Modifier
                .backgroundColor(Theme.DarkGreen.rgb)
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        )
        {
            Column(
                modifier = LandingHeroStyle.toModifier().classNames("container", "text-start").fillMaxWidth(),
                horizontalAlignment = Alignment.Start
            )
            {
                H3(
                    attrs = LandingHeroEyebrowStyle.toModifier()
                        .classNames("text-uppercase", "mb-2").toAttrs()
                ) {
                    Text("Cannabis & Marijuana Cultivation Energy Rebates")
                }

                H1(
                    attrs = LandingHeroHeadlineStyle.toModifier()
                        .classNames("mb-3").toAttrs()
                ) {
                    Text("Get Paid for Upgrading Your Cannabis Grow Facility")
                }

                P(
                    attrs = LandingHeroLeadStyle.toModifier()
                        .classNames("mb-4").toAttrs()
                ) {
                    Text("Marijuana cultivators and licensed pot grow operations are leaving thousands in unclaimed utility rebates. Green Rebates secures up to 100% funding for cannabis LED grow lights, HVAC, and dehumidification. ")
                    Link(
                        path = "/article/the-2026-guide-to-cannabis-rebates-how-to-get-paid-for-your-grow-upgrades",
                        text = "Read more about it in our article.",
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = Modifier.color(Theme.MediumGreen.rgb)
                            .role("button")
                            .attrsModifier { attr("aria-label", "Read more about it on our article.") },
                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                        openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                    )
                }

                Link(
                    path = "/contact-us",
                    text = "Get Your Free Cannabis Rebate Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaPrimaryStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Get a free cannabis and marijuana rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        // HOW IT WORKS
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-2").toAttrs()
                ) {
                    Text("How Green Rebates Works for Cannabis & Marijuana Operations")
                }
                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-5").toAttrs()
                ) {
                    Text("Three steps to maximize rebates on your pot grow, marijuana facility, or licensed cannabis operation.")
                }

                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-3", "g-4").toAttrs()) {
                    listOf(
                        Triple("1", "We Identify Every Cannabis Rebate",
                            "LED grow lights, HVAC, dehumidification systems - we find every eligible utility rebate before you spend a dollar."),
                        Triple("2", "We Handle the Paperwork",
                            "Cannabis and marijuana facilities face complex utility application processes. We manage every submission, approval, and utility communication on your behalf."),
                        Triple("3", "You Collect Your Rebate",
                            "Sit back, relax, and watch us as we help you secure up to thousands of dollars in rebates through Green Rebates.")
                    ).forEach { (num, title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(
                                attrs = Modifier
                                    .classNames("d-flex", "flex-column", "align-items-center", "text-center", "gap-3", "h-100", "py-4", "px-3")
                                    .toAttrs()
                            ) {
                                Div(
                                    attrs = LandingStepCardNumberStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center")
                                        .toAttrs()
                                ) { Text(num) }
                                H4(
                                    attrs = LandingStepCardTitleStyle.toModifier()
                                        .classNames("mb-1")
                                        .toAttrs()
                                ) { Text(title) }
                                P(
                                    attrs = LandingStepCardBodyStyle.toModifier()
                                        .classNames("mb-0")
                                        .toAttrs()
                                ) { Text(desc) }
                            }
                        }
                    }
                }
            }
        }

        // WHY NOW
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-7")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(
                    attrs = LandingDarkSectionHeadingStyle.toModifier()
                        .classNames("mb-3")
                        .toAttrs()
                ) {
                    Text("Why Cannabis & Marijuana Growers Can't Wait")
                }
                P(
                    attrs = LandingDarkBodyStyle.toModifier()
                        .classNames("mb-4", "mx-auto")
                        .toAttrs()
                ) {
                    Text("Energy is the #1 operating cost for cannabis cultivation and marijuana production. Utility rebate programs for indoor cannabis grows, pot facilities, and marijuana dispensaries are in high demand - and program funding runs out. Every month your cannabis or marijuana operation delays is money permanently left on the table.")
                }
                Link(
                    path = "/contact-us",
                    text = "Check Your Cannabis Rebates Now \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaOutlineStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Check your cannabis and marijuana rebates now") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        // WHO CAN BENEFIT
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                Div(attrs = Modifier.classNames("row", "align-items-center", "g-5").toAttrs()) {

                    // Left — image placeholder
                    Div(attrs = Modifier.classNames("col-lg-6", "order-lg-1", "order-2").toAttrs()) {
                        Img(
                            src = ImageRepo.CannabisPhoto.src,
                            alt = ImageRepo.CannabisPhoto.alt,
                            attrs = Modifier.classNames("img-fluid", "rounded-4", "shadow", "w-100")
                                .toAttrs { attr("loading", "lazy") }
                        )
                    }

                    // Right — copy + checklist + CTA
                    Div(
                        attrs = Modifier
                            .classNames("col-lg-6", "order-lg-2", "order-1", "text-lg-start", "text-center")
                            .toAttrs()
                    ) {
                        Div(
                            attrs = Modifier
                                .classNames("d-flex", "flex-column", "align-items-lg-start", "align-items-center", "gap-3")
                                .toAttrs()
                        ) {
                            H2(attrs = LandingSubSectionHeadingStyle.toModifier().toAttrs()) {
                                Text("Who Qualifies for Cannabis & Marijuana Rebates")
                            }

                            P(attrs = LandingMutedBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                Text("Whether you operate a licensed cannabis dispensary, an indoor marijuana cultivation facility, or a large-scale pot grow operation - Green Rebates finds every available rebate for your operation.")
                            }

                            Ul(
                                attrs = Modifier
                                    .classNames("list-unstyled", "d-flex", "flex-column", "gap-2", "text-start", "w-100", "mb-0")
                                    .toAttrs()
                            ) {
                                listOf(
                                    "Licensed cannabis cultivators and marijuana growers",
                                    "Indoor, greenhouse, and outdoor grow operations",
                                    "Facilities upgrading marijuana lighting, HVAC, or climate systems"
                                ).forEach { item ->
                                    Li(attrs = Modifier.classNames("d-flex", "align-items-center", "gap-2").toAttrs()) {
                                        FaCheck(
                                            modifier = LandingCheckmarkStyle.toModifier().classNames("flex-shrink-0"),
                                            size = IconSize.SM
                                        )
                                        Span { Text(item) }
                                    }
                                }
                            }

                            Link(
                                path = "/contact-us",
                                text = "Find Out If Your Facility Qualifies \u2192",
                                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                modifier = LandingCtaSecondaryStyle.toModifier()
                                    .classNames("mt-2")
                                    .role("button")
                                    .attrsModifier { attr("aria-label", "Find out if your cannabis or marijuana facility qualifies for rebates") },
                                openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                                openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                            )
                        }
                    }
                }
            }
        }

        // WHAT REBATES ARE AVAILABLE
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-2")
                        .toAttrs()
                ) {
                    Text("Types of Rebates Available")
                }
                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) {
                    Text("Utility-backed incentives specifically available to cannabis, marijuana, and pot cultivation operations.")
                }

                Div(
                    attrs = Modifier
                        .classNames("row", "row-cols-1", "row-cols-sm-2", "row-cols-lg-4", "g-4")
                        .toAttrs()
                ) {
                    listOf(
                        "LED Grow Light Rebates" to
                            "Replace HID lighting in your cannabis or marijuana grow room with high-efficiency LEDs. Most cannabis operations qualify for substantial rebates on every fixture.",
                        "Facility HVAC Incentives" to
                            "Cannabis and marijuana cultivation demand precise climate control. Upgrade your pot grow facility's HVAC and recover costs through utility incentive programs.",
                        "Dehumidification Rebates" to
                            "High-humidity cannabis and marijuana grow environments are prime candidates. Reduce energy costs on your pot grow and get paid for the upgrade.",
                        "Indoor Grow Incentives" to
                            "Targeted utility programs for licensed facilities and indoor pot cultivation operations of all sizes."
                    ).forEachIndexed { index, (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = LandingRebateCardStyle.toModifier().toAttrs()) {
                                when (index) {
                                    0 -> FaLightbulb(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    1 -> FaFan(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    2 -> FaDroplet(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    else -> FaSeedling(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                }
                                // No explicit color — inherits white from LandingRebateCardStyle on hover
                                H5(attrs = LandingRebateCardTitleStyle.toModifier().classNames("mb-2").toAttrs()) {
                                    Text(title)
                                }
                                P(attrs = LandingRebateCardBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                    Text(desc)
                                }
                            }
                        }
                    }
                }
            }
        }

        // WHY GREEN REBATES
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingDarkSectionHeadingStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) {
                    Text("Why Growers Choose Green Rebates")
                }

                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-sm-2", "g-4").toAttrs()) {
                    listOf(
                        "Cannabis Industry Expertise" to
                            "We specialize exclusively in cannabis, marijuana, and commercial horticulture operations - not general energy consulting. We know every grow rebate program available.",
                        "Direct Utility Relationships" to
                            "Our direct relationships with utilities and program administrators mean faster approvals and higher rebate payouts for your cannabis or marijuana facility.",
                        "Full-Service for Cannabis Facilities" to
                            "From cannabis grow room audits to rebate checks - we manage every step of the process for your grow, cultivation, or licensed cannabis operation.",
                        "Maximize Every Cannabis Dollar" to
                            "We find and research every cannabis and marijuana rebate program possible and how to stack incentives to get your facility the maximum possible funding."
                    ).forEach { (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = Modifier.classNames("d-flex", "gap-3", "align-items-start").toAttrs()) {
                                Div(
                                    attrs = LandingCheckIconStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center", "flex-shrink-0")
                                        .toAttrs()
                                ) { FaCheck(size = IconSize.SM) }
                                Div {
                                    H5(attrs = LandingDarkItemTitleStyle.toModifier().classNames("mb-1").toAttrs()) {
                                        Text(title)
                                    }
                                    P(attrs = LandingDarkItemBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                        Text(desc)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // TESTIMONIALS
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) {
                    Text("What Cannabis & Marijuana Growers Are Saying")
                }

                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-2", "g-4").toAttrs()) {
                    listOf(
                        "Green Rebates got our cannabis facility over $50,000 back on LED grow light and HVAC upgrades. The entire process was completely effortless - they handled everything for our marijuana operation." to
                            "Licensed Cannabis Cultivator - California",
                        "I didn't think utility rebates could cover this much for a pot grow. Green Rebates managed every application for our marijuana facility from start to finish. I'd recommend them to any cannabis operator." to
                            "Marijuana Dispensary Owner - Oregon"
                    ).forEach { (quote, attribution) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = LandingTestimonialCardStyle.toModifier().toAttrs()) {
                                Span(
                                    attrs = LandingQuoteMarkStyle.toModifier()
                                        .classNames("d-block", "mb-1")
                                        .toAttrs()
                                ) { Text("\u201C") }
                                P(attrs = LandingBodyStyle.toModifier().classNames("fst-italic", "mb-3").toAttrs()) {
                                    Text(quote)
                                }
                                Span(attrs = LandingAttributionStyle.toModifier().toAttrs()) {
                                    Text("- $attribution")
                                }
                            }
                        }
                    }
                }
            }
        }

        RelatedSection(listOf(
            "/article/the-2026-guide-to-cannabis-rebates-how-to-get-paid-for-your-grow-upgrades" to "The 2026 Guide to Cannabis Rebates: How to Get Paid for Your Grow Upgrades",
            "/blog/hall-of-flowers-2026" to "Hall of Flowers 2026",
            "/blog/lucky-leaf-2026" to "Lucky Leaf 2026 Recap | We're Lucky To Be Here",
        ))

        // FINAL CTA
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .id("contact")
                .backgroundColor(Theme.MediumGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(
                    attrs = LandingCtaHeadingStyle.toModifier()
                        .classNames("mb-3")
                        .toAttrs()
                ) {
                    Text("Ready to Claim Your Cannabis Rebates?")
                }
                P(
                    attrs = LandingCtaBodyStyle.toModifier()
                        .classNames("mb-4", "mx-auto")
                        .toAttrs()
                ) {
                    Text("Don't let your cannabis or marijuana facility miss out. Green Rebates maximizes rebates for pot grow operations, weed facilities, and licensed marijuana cultivators. Get started today.")
                }
                Link(
                    path = "/contact-us",
                    text = "Contact Us for a Free Cannabis Rebate Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaThirdStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Contact Green Rebates for a free cannabis and marijuana rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }
    }
}
